import type { SupabaseClient } from "@supabase/supabase-js";
import type {
  ExpectedRevision,
  OrganisationRepository,
  Page,
  PageRequest,
  PersistedOrganisation,
  RepositoryResult,
} from "../contracts";
import type { Database } from "../database.generated";
import type { OrganisationRow } from "../database.types";
import { mapOrganisationRow } from "../mappers";
import { organisationDetailsCodec } from "./codecs";
import { revisionConflict } from "./errors";
import {
  createIdPage,
  cursorId,
  databaseFailure,
  ID_ORDER,
  notFound,
  validationFailure,
} from "./repository-helpers";

export class SupabaseOrganisationRepository implements OrganisationRepository {
  constructor(private readonly client: SupabaseClient<Database>) {}

  async getById(id: string): Promise<RepositoryResult<PersistedOrganisation>> {
    const { data, error } = await this.client.from("organisations").select("*").eq("id", id).maybeSingle();
    if (error) return databaseFailure(error, "The organisation could not be read.");
    if (!data) return notFound("Organisation");
    return mapOrganisationRow(data as OrganisationRow);
  }

  async list(page: PageRequest): Promise<RepositoryResult<Page<PersistedOrganisation>>> {
    const after = cursorId(page);
    if (!after.ok) return after;
    let query = this.client
      .from("organisations")
      .select("*")
      .order("id", { ascending: true })
      .limit(page.limit + 1);
    if (after.value) query = query.gt("id", after.value);
    const { data, error } = await query;
    if (error) return databaseFailure(error, "Organisations could not be listed.");
    return createIdPage(data ?? [], page, (row) => mapOrganisationRow(row as OrganisationRow));
  }

  async create(
    record: Omit<PersistedOrganisation, "revision" | "updatedAt">,
  ): Promise<RepositoryResult<PersistedOrganisation>> {
    const details = organisationDetailsCodec.decode(record.details, record.details.schemaVersion);
    if (!details.ok) return validationFailure(details.error.message);
    const { data, error } = await this.client
      .from("organisations")
      .insert({
        id: record.id,
        name: record.name,
        lifecycle_status: record.lifecycleStatus,
        details_schema_version: details.value.schemaVersion,
        details: details.value,
        archived_at: record.archivedAt ?? null,
      })
      .select("*")
      .single();
    if (error) return databaseFailure(error, "The organisation could not be created.");
    return mapOrganisationRow(data as OrganisationRow);
  }

  async update(
    id: string,
    changes: Partial<Pick<PersistedOrganisation, "name" | "lifecycleStatus" | "details" | "archivedAt">>,
    options: ExpectedRevision,
  ): Promise<RepositoryResult<PersistedOrganisation>> {
    const row: Database["public"]["Tables"]["organisations"]["Update"] = {};
    if (changes.name !== undefined) row.name = changes.name;
    if (changes.lifecycleStatus !== undefined) row.lifecycle_status = changes.lifecycleStatus;
    if (changes.details !== undefined) {
      const details = organisationDetailsCodec.decode(changes.details, changes.details.schemaVersion);
      if (!details.ok) return validationFailure(details.error.message);
      row.details_schema_version = details.value.schemaVersion;
      row.details = details.value;
    }
    if ("archivedAt" in changes) row.archived_at = changes.archivedAt ?? null;
    if (!Object.keys(row).length) return validationFailure("At least one organisation change is required.");

    const { data, error } = await this.client
      .from("organisations")
      .update(row)
      .eq("id", id)
      .eq("revision", options.expectedRevision)
      .select("*")
      .maybeSingle();
    if (error) return databaseFailure(error, "The organisation could not be updated.");
    if (data) return mapOrganisationRow(data as OrganisationRow);
    const current = await this.getById(id);
    return current.ok ? { ok: false, error: revisionConflict() } : current;
  }

  archive(
    id: string,
    archivedAt: string,
    options: ExpectedRevision,
  ): Promise<RepositoryResult<PersistedOrganisation>> {
    return this.update(id, { archivedAt }, options);
  }
}

export const organisationRepositoryOrder = ID_ORDER;
